import React, { useState, useRef } from "react";
import {
  Paper,
  Typography,
  Divider,
  Button,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  LinearProgress,
  Alert,
  AlertTitle,
  Tooltip,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SplitscreenIcon from "@mui/icons-material/Splitscreen";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { NotificationManager } from "react-notifications";
import { UploadOrderAttachment } from "../../services/OrderService";

// Dropdown values map directly to the category strings the backend expects.
const CHAIN_TYPE_OPTIONS = [
  { value: "ETKChainSplitterDoc", label: "ETK" },
  { value: "NonETKChainSplitterDoc", label: "Non-ETK" },
];

const EtkChainCocUploader = () => {
  const fileInputRef = useRef(null);

  // Single file only: { file, status: "pending"|"uploading"|"done"|"error", error: null|string }
  const [queuedFile, setQueuedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Chain type dropdown — starts unselected ("") on purpose so a user can't
  // accidentally submit without consciously picking ETK or Non-ETK. The value
  // IS the category string sent straight to the backend.
  const [chainType, setChainType] = useState("");
  const hasChainType = chainType !== "";

  // ── File helpers ─────────────────────────────────────────────────────────────
  const applyFile = (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      NotificationManager.warning("Only PDF files are accepted.");
      return;
    }
    setQueuedFile({ file, status: "pending", error: null });
  };

  const handleFileSelect = (event) => {
    applyFile(event.target.files[0]);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (dropDisabled) return;
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 1)
      NotificationManager.warning("Only one file at a time. The first PDF was used.");
    applyFile(files[0]);
  };

  const handleDragOver = (event) => event.preventDefault();

  // ── Upload ───────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!queuedFile || queuedFile.status !== "pending") return;
    if (!hasChainType) return; // hard stop — should already be disabled at the button too

    setIsUploading(true);
    setQueuedFile((prev) => ({ ...prev, status: "uploading" }));

    try {
      const formData = new FormData();
      formData.append("file", queuedFile.file);
      formData.append("title", queuedFile.file.name);
      formData.append("category", chainType);
      formData.append("orderNumber", 0);

      await UploadOrderAttachment(formData);

      setQueuedFile((prev) => ({ ...prev, status: "done" }));
      NotificationManager.success(
        "File sent to splitter queue — chains will be processed within 1 minute."
      );
    } catch (error) {
      const message = error?.message || "Upload failed";
      setQueuedFile((prev) => ({ ...prev, status: "error", error: message }));
      NotificationManager.error(`Upload failed: ${queuedFile.file.name}`);
    } finally {
      setIsUploading(false);
    }
  };

  // ── Derived state ────────────────────────────────────────────────────────────
  const isPending = queuedFile?.status === "pending";
  const isDone = queuedFile?.status === "done";
  const isError = queuedFile?.status === "error";
  const dropDisabled = isUploading || isDone;

  // Submit is only enabled when there's a pending file AND a chain type has
  // actually been chosen — this is the extra check requested so the form can't
  // silently go out with a default/unintended chain type.
  const canSubmit = isPending && hasChainType && !isUploading;

  const statusIcon = () => {
    if (!queuedFile) return null;
    if (queuedFile.status === "uploading") return <CircularProgress size={18} />;
    if (isDone) return <CheckCircleIcon sx={{ color: "success.main" }} />;
    if (isError) return <ErrorIcon sx={{ color: "error.main" }} />;
    return <PictureAsPdfIcon sx={{ color: "error.main" }} />;
  };

  const statusColor = () => {
    if (isDone) return "success.main";
    if (isError) return "error.main";
    if (queuedFile?.status === "uploading") return "primary.main";
    return "text.primary";
  };

  const submitTooltip = () => {
    if (!queuedFile) return "Select a PDF file first";
    if (!isPending) return "File already processed";
    if (!hasChainType) return "Select a chain type (ETK or Non-ETK) first";
    return "Send this file to the Chain Splitter queue";
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        p: 3,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* ── Page header ── */}
      <Box>
        <Typography variant="h5" fontWeight={600} color="primary">
          Chain of Custody Upload
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Upload a scanned Chain of Custody PDF to be automatically split and
          attached to the corresponding lab orders. This works for both ETK
          and Non-ETK Chains of Custody — just select
          the correct chain type below before sending.
        </Typography>
      </Box>

      <Divider />

      {/* ── Upload area ── */}
      <Paper elevation={2} sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            Upload File
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a single scanned PDF. It may contain multiple Chains of Custody —
            the splitter will separate each one automatically, using a QR code for
            ETK kits or a barcode for Non-ETK kits.
          </Typography>
        </Box>

        {/* Drop zone */}
        <Box
          onDrop={handleDrop}
          onDragOver={dropDisabled ? undefined : handleDragOver}
          onClick={dropDisabled ? undefined : () => fileInputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: dropDisabled ? "action.disabled" : "primary.main",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            cursor: dropDisabled ? "default" : "pointer",
            backgroundColor: dropDisabled
              ? "rgba(0,0,0,0.02)"
              : "rgba(25,118,210,0.04)",
            "&:hover": dropDisabled ? {} : { backgroundColor: "rgba(25,118,210,0.08)" },
            transition: "background-color 0.2s",
          }}
        >
          <CloudUploadIcon
            sx={{
              fontSize: 48,
              color: dropDisabled ? "action.disabled" : "primary.main",
              mb: 1,
            }}
          />
          <Typography
            variant="body1"
            fontWeight={500}
            color={dropDisabled ? "text.disabled" : "primary"}
          >
            {isDone
              ? "File uploaded successfully"
              : isUploading
                ? "Uploading…"
                : "Click or drag & drop a PDF file here"}
          </Typography>
          {!dropDisabled && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              One PDF file at a time · No file size limit
            </Typography>
          )}
        </Box>

        {/* Hidden file input — single, PDF only */}
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        {/* Selected file row */}
        {queuedFile && (
          <>
            {isUploading && <LinearProgress sx={{ borderRadius: 1 }} />}
            <List dense disablePadding>
              <ListItem
                disableGutters
                sx={{
                  borderRadius: 1,
                  px: 1,
                  backgroundColor: isDone
                    ? "rgba(46,125,50,0.06)"
                    : isError
                      ? "rgba(211,47,47,0.06)"
                      : "transparent",
                }}
                secondaryAction={
                  !isUploading && !isDone && (
                    <Tooltip title="Remove file" placement="left">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => setQueuedFile(null)}
                      >
                        <DeleteIcon fontSize="small" sx={{ color: "text.secondary" }} />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                <ListItemIcon sx={{ minWidth: 32 }}>{statusIcon()}</ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{ color: statusColor(), fontWeight: isUploading ? 600 : 400 }}
                      noWrap
                    >
                      {queuedFile.file.name}
                    </Typography>
                  }
                  secondary={
                    isDone
                      ? "Sent to splitter queue — chains will be processed within 1 minute"
                      : isError
                        ? queuedFile.error
                        : `${(queuedFile.file.size / 1024).toFixed(1)} KB · Ready to upload`
                  }
                />
              </ListItem>
            </List>
          </>
        )}

        {/* Chain type dropdown — sits directly above the submit button. Starts
            unselected on purpose; the submit button stays disabled until a
            value is chosen, so this can't be silently skipped. */}
        <FormControl
          size="small"
          sx={{ maxWidth: 260 }}
          required
          disabled={isUploading}
        >
          <InputLabel id="chain-type-label">Chain Type</InputLabel>
          <Select
            labelId="chain-type-label"
            label="Chain Type"
            value={chainType}
            displayEmpty
            onChange={(e) => setChainType(e.target.value)}
          >
            <MenuItem value="">
              <em></em>
            </MenuItem>
            {CHAIN_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={submitTooltip()} placement="top">
            <span>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={
                  isUploading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CloudUploadIcon />
                  )
                }
                onClick={handleUpload}
                disabled={!canSubmit}
              >
                {isUploading ? "Uploading…" : "Send to Splitter Queue"}
              </Button>
            </span>
          </Tooltip>

          {(isDone || isError) && (
            <Tooltip title="Clear and upload a new file" placement="top">
              <Button
                variant="outlined"
                onClick={() => {
                  setQueuedFile(null);
                }}
              >
                Upload Another
              </Button>
            </Tooltip>
          )}
        </Box>
      </Paper>

      {/* ── Important note ── */}
      <Alert severity="info" icon={<InfoOutlinedIcon />}>
        <AlertTitle>Before You Upload</AlertTitle>
        Make sure you've picked the correct chain type above — it tells the
        splitter which format to expect, and the file can't be sent until it's
        selected. <strong>ETK kits:</strong> every page must have a visible,
        unspoiled QR code containing the order number. <strong>Non-ETK kits:</strong>{" "}
        only the first page of each order's Chain of Custody needs a visible
        barcode with the order number — any additional pages for that same order
        (continuation pages) don't need one and will be grouped with it
        automatically. Either way, any page that can't be resolved will be saved
        separately for manual review and will <strong>not</strong> be automatically
        attached to an order.
      </Alert>

      {/* ── How it works ── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <InfoOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600} color="primary">
            How This Works
          </Typography>
        </Box>
        <Stack spacing={1.5}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Chip label="1" size="small" color="primary" sx={{ mt: 0.2, minWidth: 28 }} />
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Scan &amp; Upload
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scan multiple Chains of Custody into a single PDF — either all ETK or
                all Non-ETK — and upload that PDF here, with the chain type dropdown
                set correctly above.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Chip label="2" size="small" color="primary" sx={{ mt: 0.2, minWidth: 28 }} />
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flex: 1 }}>
              <SplitscreenIcon sx={{ color: "text.secondary", fontSize: 18, mt: 0.25 }} />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Auto Split{" "}
                  <Tooltip
                    title="Runs every minute. For ETK kits, reads the QR code on each page (every page decodes its own order). For Non-ETK kits, reads the barcode on each order's first page and groups any continuation pages with it. Either way, the PDF is split into individual files named by order number."
                    arrow
                    placement="top"
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: 14, color: "text.disabled", verticalAlign: "middle", cursor: "help" }}
                    />
                  </Tooltip>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The <strong>Chain Splitter</strong> background job (runs every minute)
                  reads the QR code (ETK) or barcode (Non-ETK) on each page, extracts
                  the order number, and splits the PDF into individual files — one
                  per order.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Chip label="3" size="small" color="primary" sx={{ mt: 0.2, minWidth: 28 }} />
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flex: 1 }}>
              <AttachFileIcon sx={{ color: "text.secondary", fontSize: 18, mt: 0.25 }} />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Auto Attach{" "}
                  <Tooltip
                    title="Runs every minute. Matches each split PDF (by order number) to a lab order in the system and attaches the CoC document to it — same job for both ETK and Non-ETK files."
                    arrow
                    placement="top"
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: 14, color: "text.disabled", verticalAlign: "middle", cursor: "help" }}
                    />
                  </Tooltip>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The <strong>Chain Attacher</strong> background job (runs every minute)
                  picks up each split PDF, from either kit type, and attaches it to the
                  matching lab order in the system automatically.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default EtkChainCocUploader;